import { z } from 'zod'

const SEPARATOR = '|'
export const CompositeKeyIdSchema = z.templateLiteral([z.string(), z.literal(SEPARATOR), z.string()])
export type CompositeKeyId = z.infer<typeof CompositeKeyIdSchema>

export const ID_PREFIXES: string[] = []

export function buildKey<P, S>(partitionKey: P, sortKey: S): CompositeKeyId {
  return `${partitionKey}${SEPARATOR}${sortKey}`
}

export function isId<
  T extends CustomIdType<string, string> | z.ZodUnion<readonly [CustomIdType<string, ':'>, CustomIdType<string, '.'>]>,
>(schema: T): (val: unknown) => val is z.infer<T> {
  return (val: unknown): val is z.infer<T> => schema.safeParse(val).success
}

type CustomIdType<T extends string, S extends string> = z.ZodTemplateLiteral<`${T}${S}${string}`>

/** OpenAPI description of an ID format, e.g. "`U.` or `U:` prefix followed by a unique suffix, e.g. `U.123-4aBc_d`" */
export function idFormatDescription(prefix: string, separators: readonly string[]): string {
  const prefixes = separators.map((separator) => `\`${prefix}${separator}\``).join(' or ')
  return `${prefixes} prefix followed by a unique suffix, e.g. \`${prefix}${separators[0]}123-4aBc_d\``
}

function idDescription(entity: string, prefix: string, separators: readonly string[]): string {
  return `Unique identifier for the ${entity}: ${idFormatDescription(prefix, separators)}`
}
export function customIdSchema<T extends string, S extends string>(prefix: T, separator: S): CustomIdType<T, S>
export function customIdSchema(prefix: string, separator: string): CustomIdType<string, string> {
  if (!ID_PREFIXES.includes(prefix)) {
    ID_PREFIXES.push(prefix)
  }
  return z.templateLiteral([`${prefix}${separator}`, z.string()]).meta({
    description: `Terros ID: ${idFormatDescription(prefix, [separator])}`,
    example: `${prefix}${separator}123-4aBc_d`,
  })
}

/** V2 Ids are only separated by a '.' */
export function idSchemaV2<T extends string>(prefix: T): CustomIdType<T, '.'> {
  return customIdSchema(prefix, '.').meta({
    id: `${prefix}Id`,
    description: `Terros ID: ${idFormatDescription(prefix, ['.'])}`,
    example: `${prefix}.123-4aBc_d`,
  })
}

/** Legacy Ids that were merged with V2 Ids. Separators are ':' and '.'. This should not be used for new IDs. */
type LegacyIdType<T extends string, Separator extends string> = z.ZodTemplateLiteral<`${T}${Separator}${string}`>
const DEFAULT_SEPARATORS = ['.', ':'] as const
export function idSchemaV1<T extends string>(prefix: T): LegacyIdType<T, (typeof DEFAULT_SEPARATORS)[number]>
export function idSchemaV1<T extends string, Separator extends string>(
  prefix: T,
  separators: Separator[]
): LegacyIdType<T, Separator>
export function idSchemaV1<T extends string>(prefix: T, separators?: readonly string[]): LegacyIdType<T, string> {
  if (!ID_PREFIXES.includes(prefix)) {
    ID_PREFIXES.push(prefix)
  }
  const seps = separators ?? DEFAULT_SEPARATORS
  const SepSchema = z.union(seps.map((s) => z.literal(s)))
  return z.templateLiteral([prefix, SepSchema, z.string()]).meta({
    description: `Terros ID: ${idFormatDescription(prefix, seps)}`,
    example: `${prefix}${seps[0]}123-4aBc_d`,
    id: `${prefix}Id`,
  })
}

export const UserIdSchema = idSchemaV1('U').meta({
  id: 'userId',
  title: 'User ID',
  description: idDescription('user', 'U', DEFAULT_SEPARATORS),
})
export type UserId = z.infer<typeof UserIdSchema>
export const isUserId = isId(UserIdSchema)

export const TeamIdSchema = idSchemaV1('Team').meta({
  id: 'teamId',
  title: 'Team ID',
  description: idDescription('team', 'Team', DEFAULT_SEPARATORS),
})
export type TeamId = z.infer<typeof TeamIdSchema>
export const isTeamId = isId(TeamIdSchema)

export const CompanyIdSchema = idSchemaV1('C').meta({
  id: 'companyId',
  title: 'Company ID',
  description: idDescription('company', 'C', DEFAULT_SEPARATORS),
})
export type CompanyId = z.infer<typeof CompanyIdSchema>
export const isCompanyId = isId(CompanyIdSchema)

export const AttachmentIdSchema = idSchemaV1('Attachment')
export type AttachmentId = z.infer<typeof AttachmentIdSchema>
export const isAttachmentId = isId(AttachmentIdSchema)

export const CardSendIdSchema = z.string()
export type CardSendId = z.infer<typeof CardSendIdSchema>

export const CardAttachmentIdSchema = idSchemaV2('CardAttachment')
export type CardAttachmentId = z.infer<typeof CardAttachmentIdSchema>
export const isCardAttachmentId = isId(CardAttachmentIdSchema)

export const AccountStatusIdSchema = idSchemaV2('AS').meta({
  id: 'accountStatusId',
  title: 'Account Status ID',
  description: idDescription('account status', 'AS', ['.']),
  deprecated: true,
})
export type AccountStatusId = z.infer<typeof AccountStatusIdSchema>
export const isAccountStatusId = isId(AccountStatusIdSchema)
export type WithAccountStatusId = {
  statusId: AccountStatusId
}

export const EntityIdSchema = z.union([UserIdSchema, TeamIdSchema, CompanyIdSchema])
export type EntityId = z.infer<typeof EntityIdSchema>
export const isEntityId = (val: unknown): val is EntityId => EntityIdSchema.safeParse(val).success

export type WithEntityId = {
  entityId: EntityId
}

export const CanadaIdSchema = idSchemaV2('Can')
export type CanadaId = z.infer<typeof CanadaIdSchema>
export const isCanadaLocationId = isId(CanadaIdSchema)

export const PuertoRicoIdSchema = idSchemaV2('PR')
export type PuertoRicoId = z.infer<typeof PuertoRicoIdSchema>
export const isPuertoRicoLocationId = isId(PuertoRicoIdSchema)

export type ExternalLocationId = CanadaId | FirstAmericanId | PuertoRicoId
export const isExternalLocationId = (val: unknown): val is ExternalLocationId =>
  CanadaIdSchema.safeParse(val).success ||
  FirstAmericanIdSchema.safeParse(val).success ||
  PuertoRicoIdSchema.safeParse(val).success

export const TerrosLocationIdSchema = idSchemaV1('TLoc')
export type TerrosLocationId = z.infer<typeof TerrosLocationIdSchema>
export const isTerrosLocationId = isId(TerrosLocationIdSchema)

export const CustomLocationIdSchema = idSchemaV1('LocNew')
export type CustomLocationId = z.infer<typeof CustomLocationIdSchema>
export const isCustomLocationId = isId(CustomLocationIdSchema)

export const LegacyCompanyLocationIdSchema = customIdSchema('Loc', ':')
export type LegacyCompanyLocationId = z.infer<typeof LegacyCompanyLocationIdSchema>
export const isLegacyCompanyLocationId = isId(LegacyCompanyLocationIdSchema)

export const FirstAmericanIdSchema = idSchemaV2('FA')
export type FirstAmericanId = z.infer<typeof FirstAmericanIdSchema>
export const isFirstAmericanId = isId(FirstAmericanIdSchema)

export const LocationIdSchema = z
  .union([
    CanadaIdSchema,
    CustomLocationIdSchema,
    LegacyCompanyLocationIdSchema,
    TerrosLocationIdSchema,
    FirstAmericanIdSchema,
    PuertoRicoIdSchema,
  ])
  .meta({
    id: 'locationId',
    title: 'Location ID',
    description:
      'Unique identifier for the location. Usually a Terros location ID such as `TLoc.123-4aBc_d`. Custom locations use `LocNew.`, and other address sources use `Can.`, `FA.`, `PR.`, or legacy `Loc:` prefixes.',
    example: 'TLoc.123-4aBc_d',
  })
export type LocationId = z.infer<typeof LocationIdSchema>
export const isLocationId = (val: unknown): val is LocationId => LocationIdSchema.safeParse(val).success

export const AccountIdSchema = idSchemaV1('Account').meta({
  id: 'accountId',
  title: 'Account ID',
  description: idDescription('account', 'Account', DEFAULT_SEPARATORS),
})
export type AccountId = z.infer<typeof AccountIdSchema>
export const isAccountId = isId(AccountIdSchema)
export const WithAccountIdSchema = z.object({
  accountId: AccountIdSchema,
})
export type WithAccountId = z.infer<typeof WithAccountIdSchema>
export type WithOptionalAccountId = {
  accountId?: AccountId
}

export const InterviewIdSchema = idSchemaV1('Interview')
export type InterviewId = z.infer<typeof InterviewIdSchema>
export const isInterviewId = isId(InterviewIdSchema)

export const WithInterviewIdSchema = z.object({
  interviewId: InterviewIdSchema,
})
export type WithInterviewId = z.infer<typeof WithInterviewIdSchema>

export const ProgramIdSchema = idSchemaV2('Program')
export type ProgramId = z.infer<typeof ProgramIdSchema>

export const WorkflowIdSchema = idSchemaV2('WF')
export type WorkflowId = z.infer<typeof WorkflowIdSchema>
export const isWorkflowId = isId(WorkflowIdSchema)
export const WithWorkflowIdSchema = z.object({
  workflowId: WorkflowIdSchema,
})
export type WithWorkflowId = z.infer<typeof WithWorkflowIdSchema>

export const WorkflowStageIdSchema = idSchemaV2('S')
export type WorkflowStageId = z.infer<typeof WorkflowStageIdSchema>
export const isWorkflowStageId = isId(WorkflowStageIdSchema)

export const WorkflowActionIdSchema = idSchemaV2('A')
export type WorkflowActionId = z.infer<typeof WorkflowActionIdSchema>
export const isWorkflowActionId = isId(WorkflowActionIdSchema)
